import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Printer, RefreshCw, Package, FlaskConical, CheckCircle2, Phone, Mail, LogOut } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const TABS = [
  { key: "dropped_off", label: "Dropped Off", icon: Package },
  { key: "in_grading", label: "In Grading", icon: FlaskConical },
  { key: "completed", label: "Completed", icon: CheckCircle2 },
];

export default function Convention() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("dropped_off");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/convention/orders");
      setOrders(data);
    } catch {
      toast.error("Could not load orders.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await api.patch(`/convention/orders/${id}/status`, { status });
      toast.success(status === "in_grading" ? "Marked In Grading — customer texted." : status === "completed" ? "Marked Completed — pickup text sent." : "Updated.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed.");
    } finally {
      setBusy(null);
    }
  };

  const counts = TABS.reduce((a, t) => ({ ...a, [t.key]: orders.filter((o) => o.status === t.key).length }), {});
  const filtered = orders.filter((o) => o.status === tab);

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="bg-[#0A0A0A] text-white">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <div>
            <div className="font-display text-xl tracking-tight" style={{ fontWeight: 900 }}>Convention Fulfillment</div>
            <div className="text-white/50 text-xs">{user?.email}</div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="conv-refresh" onClick={load} className="p-2.5 rounded-full bg-white/10 active:scale-95"><RefreshCw size={18} className={loading ? "animate-spin" : ""} /></button>
            <button data-testid="conv-logout" onClick={logout} className="p-2.5 rounded-full bg-white/10 active:scale-95"><LogOut size={18} /></button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-5">
        <div className="flex gap-2 mb-5">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`conv-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold transition-colors ${tab === t.key ? "bg-[#0A0A0A] text-white" : "bg-white text-neutral-500 border border-neutral-200"}`}>
              <t.icon size={16} /> {t.label} <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${tab === t.key ? "bg-white/20" : "bg-neutral-100"}`}>{counts[t.key] || 0}</span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="text-center text-neutral-400 py-16">No orders in this stage.</p>
        ) : (
          <div className="space-y-3">
            {filtered.map((o) => (
              <div key={o.id} data-testid={`conv-order-${o.id}`} className="bg-white rounded-2xl border border-neutral-200 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-lg text-[#0A0A0A]" style={{ fontWeight: 900 }}>{o.order_no} · {o.customer_name}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-neutral-500">
                      <span className="flex items-center gap-1"><Phone size={13} /> {o.phone}</span>
                      {o.email ? <span className="flex items-center gap-1"><Mail size={13} /> {o.email}</span> : null}
                    </div>
                  </div>
                  <Link to={`/convention/label/${o.id}`} data-testid={`conv-print-${o.id}`} target="_blank"
                    className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-[#0A0A0A] text-[#0A0A0A] text-sm font-bold rounded-full active:scale-95">
                    <Printer size={15} /> Label
                  </Link>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {o.items.map((it, i) => (<span key={i} className="px-3 py-1.5 rounded-lg bg-neutral-100 text-sm text-neutral-700">{it}</span>))}
                </div>

                <div className="mt-4 flex gap-2">
                  {o.status === "dropped_off" && (
                    <button data-testid={`conv-start-${o.id}`} onClick={() => setStatus(o.id, "in_grading")} disabled={busy === o.id}
                      className="flex-1 py-3 bg-[#0A0A0A] text-white font-bold rounded-full active:scale-[0.99] disabled:opacity-50">{busy === o.id ? "…" : "Mark In Grading → text customer"}</button>
                  )}
                  {o.status === "in_grading" && (
                    <button data-testid={`conv-complete-${o.id}`} onClick={() => setStatus(o.id, "completed")} disabled={busy === o.id}
                      className="flex-1 py-3 bg-emerald-600 text-white font-bold rounded-full active:scale-[0.99] disabled:opacity-50">{busy === o.id ? "…" : "Mark Completed → text pickup"}</button>
                  )}
                  {o.status === "completed" && (
                    <div className="flex-1 py-3 text-center text-emerald-700 font-bold bg-emerald-50 rounded-full">Completed · Picked up at booth</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
